import { CanonicalEncoder, utf8 } from "./encoding";
import { hashToField, parseField } from "./field";

export enum ActionKind {
  ReserveWithdraw = 1,
  ReserveToSubwallet = 2,
  ReserveMerge = 3,
  CreateSubwallet = 10,
  SubwalletCall = 11,
  SubwalletBatch = 12,
  SubwalletToReserve = 13,
}

/** Initial notes bind their recovery payload and deployment without identifying the payer as owner. */
export const initialNoteHash = async (value: {
  chainId: string;
  proxyContract: string;
  kind: "reserve" | "control";
  commitment: string;
  ciphertext: Uint8Array;
}): Promise<string> =>
  hashToField(
    new CanonicalEncoder()
      .pushBytes(utf8("redacted-initial-note"))
      .pushU16(2)
      .pushString(value.chainId)
      .pushString(value.proxyContract)
      .pushU16(value.kind === "reserve" ? 1 : 2)
      .pushField(value.commitment)
      .pushField(await hashToField(value.ciphertext))
      .finish()
  );

export type ActionDigest = {
  protocolVersion?: 2;
  chainId: string;
  proxyContract: string;
  actionKind: ActionKind;
  canonicalPayload: Uint8Array;
  outputCommitments: string[];
  encryptedOutputPayloads: Uint8Array[];
  relayerAddress: string;
  relayerFee: bigint;
  expiryHeight: bigint;
};

export const actionHash = async (value: ActionDigest): Promise<string> => {
  const encoder = new CanonicalEncoder()
    .pushBytes(utf8("redacted-v2"))
    .pushU16(value.protocolVersion ?? 2)
    .pushString(value.chainId)
    .pushString(value.proxyContract)
    .pushU16(value.actionKind)
    .pushBytes(value.canonicalPayload)
    .pushU32(value.outputCommitments.length);
  value.outputCommitments.forEach((commitment) => {
    parseField(commitment);
    encoder.pushField(commitment);
  });
  encoder.pushU32(value.encryptedOutputPayloads.length);
  for (const payload of value.encryptedOutputPayloads) {
    encoder.pushField(await hashToField(payload));
  }
  encoder
    .pushString(value.relayerAddress)
    .pushU128(value.relayerFee)
    .pushU64(value.expiryHeight);
  return hashToField(encoder.finish());
};

export const reserveTransferPayload = (value: {
  denom: string;
  destination: string;
  spendAmount: bigint;
}): Uint8Array =>
  new CanonicalEncoder()
    .pushString(value.denom)
    .pushString(value.destination)
    .pushU128(value.spendAmount)
    .finish();

export type V2Coin = { denom: string; amount: string };

export type FeeAuthorization = Readonly<{
  policy_version: number;
  protocol_fee_bps: number;
  /** Exact debits for strict product rules; absent on legacy fee policies. */
  protocol_fees?: readonly Readonly<V2Coin>[];
}>;

/** Matches the proxy's fee-policy wrapper; existing unbound payloads stay unchanged. */
export const bindFeePolicy = (
  payload: Uint8Array,
  authorization: FeeAuthorization,
  feeCollector: string
): Uint8Array => {
  if (
    !Number.isSafeInteger(authorization.policy_version) ||
    authorization.policy_version < 0 ||
    !Number.isInteger(authorization.protocol_fee_bps) ||
    authorization.protocol_fee_bps < 0 ||
    authorization.protocol_fee_bps > 1_000 ||
    !feeCollector
  ) {
    throw new Error("Invalid protocol fee authorization");
  }
  const encoder = new CanonicalEncoder()
    .pushBytes(utf8("redacted-fee-policy"))
    .pushU64(BigInt(authorization.policy_version))
    .pushU16(authorization.protocol_fee_bps)
    .pushString(feeCollector)
    .pushBytes(payload);
  if (authorization.protocol_fees !== undefined) {
    const fees = authorization.protocol_fees;
    if (
      !Array.isArray(fees) ||
      fees.length > 256 ||
      fees.some(
        (coin) =>
          typeof coin?.denom !== "string" ||
          !/^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/.test(coin.denom) ||
          typeof coin.amount !== "string" ||
          coin.amount.length > 39 ||
          !/^[1-9]\d*(?![\s\S])/.test(coin.amount)
      )
    )
      throw new Error("Invalid authorized protocol debits");
    encoder.pushBytes(utf8("redacted-protocol-debits"));
    pushCoins(encoder, fees);
  }
  return encoder.finish();
};

export type V2ThorchainAsset = {
  chain: string;
  symbol: string;
  ticker: string;
  secured: boolean;
};

export type V2Call =
  | { bank_send: { to_address: string; amount: V2Coin[] } }
  | { wasm_execute: { contract_addr: string; msg: string; funds: V2Coin[] } }
  | {
      thorchain_deposit: {
        denom: string;
        asset: V2ThorchainAsset;
        amount: string;
        memo: string;
      };
    };

const ZERO_AMOUNT_LP_WITHDRAWAL =
  /^-:([A-Z0-9][A-Z0-9._/-]{0,127}):(0|[1-9]\d*)$/;

export const isZeroAmountThorchainLiquidityWithdrawal = (
  amount: bigint,
  memo: string
): boolean => {
  if (amount !== 0n) return false;
  const match = memo.match(ZERO_AMOUNT_LP_WITHDRAWAL);
  if (!match) return false;
  const bps = BigInt(match[2]);
  return bps >= 1n && bps <= 10_000n;
};

const pushCoins = (
  encoder: CanonicalEncoder,
  coins: readonly V2Coin[]
): void => {
  encoder.pushU32(coins.length);
  let previous = "";
  for (const coin of coins) {
    if (!coin.denom || coin.denom <= previous || !/^\d+$/.test(coin.amount)) {
      throw new Error(
        "Coins must be nonzero, unique, and sorted by denomination"
      );
    }
    const amount = BigInt(coin.amount);
    if (amount <= 0n)
      throw new Error(
        "Coins must be nonzero, unique, and sorted by denomination"
      );
    encoder.pushString(coin.denom).pushU128(amount);
    previous = coin.denom;
  }
};

export const canonicalCalls = (calls: readonly V2Call[]): Uint8Array => {
  if (calls.length < 1 || calls.length > 16)
    throw new Error("Invalid private call count");
  const encoder = new CanonicalEncoder().pushU32(calls.length);
  for (const call of calls) {
    if ("bank_send" in call) {
      encoder.pushU16(1).pushString(call.bank_send.to_address);
      pushCoins(encoder, call.bank_send.amount);
    } else if ("wasm_execute" in call) {
      const message = Uint8Array.from(
        atob(call.wasm_execute.msg),
        (character) => character.charCodeAt(0)
      );
      if (message.length > 65_536)
        throw new Error("Private call message is too large");
      encoder
        .pushU16(2)
        .pushString(call.wasm_execute.contract_addr)
        .pushBytes(message);
      pushCoins(encoder, call.wasm_execute.funds);
    } else {
      const { denom, asset, amount, memo } = call.thorchain_deposit;
      const component = (value: string): boolean =>
        value.length >= 1 &&
        value.length <= 128 &&
        /^[A-Z0-9][A-Z0-9._:/-]*$/.test(value);
      if (
        !component(asset.chain) ||
        !component(asset.symbol) ||
        !component(asset.ticker) ||
        asset.ticker !== asset.symbol.split("-")[0] ||
        !/^\d+$/.test(amount) ||
        (BigInt(amount) === 0n &&
          !isZeroAmountThorchainLiquidityWithdrawal(BigInt(amount), memo)) ||
        memo.length < 1 ||
        memo.length > 250 ||
        !/^[\x20-\x7e]+$/.test(memo)
      ) {
        throw new Error("Invalid THORChain deposit call");
      }
      const expectedDenom = asset.secured
        ? asset.chain === "THOR"
          ? ""
          : `${asset.chain.toLowerCase()}-${asset.symbol.toLowerCase()}`
        : asset.chain !== "THOR"
          ? ""
          : asset.symbol === "RUNE"
            ? "rune"
            : asset.symbol === "RUJI"
              ? "x/ruji"
              : asset.symbol === "TCY"
                ? "tcy"
                : asset.symbol === "TOR"
                  ? "tor"
                  : `thor.${asset.symbol.toLowerCase()}`;
      if (denom !== expectedDenom)
        throw new Error("THORChain asset and denom do not match");
      encoder
        .pushU16(3)
        .pushString(denom)
        .pushString(asset.chain)
        .pushString(asset.symbol)
        .pushString(asset.ticker)
        .pushU16(asset.secured ? 1 : 0)
        .pushU128(BigInt(amount))
        .pushString(memo);
    }
  }
  return encoder.finish();
};

export const subwalletCallPayload = (value: {
  subwallet: string;
  calls: readonly V2Call[];
  relayerFee?: V2Coin;
}): Uint8Array => {
  const encoder = new CanonicalEncoder()
    .pushString(value.subwallet)
    .pushBytes(canonicalCalls(value.calls));
  if (value.relayerFee) {
    if (
      !/^\d+$/.test(value.relayerFee.amount) ||
      BigInt(value.relayerFee.amount) <= 0n
    ) {
      throw new Error("Invalid relayer fee");
    }
    encoder
      .pushU16(1)
      .pushString(value.relayerFee.denom)
      .pushU128(BigInt(value.relayerFee.amount));
  } else {
    encoder.pushU16(0);
  }
  return encoder.finish();
};

export const createSubwalletPayload = (
  salt: Uint8Array,
  subwallet: string
): Uint8Array =>
  new CanonicalEncoder().pushBytes(salt).pushString(subwallet).finish();

export const shieldPayload = (value: {
  subwallet: string;
  amount: V2Coin;
  reserveCommitment: string;
}): Uint8Array =>
  new CanonicalEncoder()
    .pushString(value.subwallet)
    .pushString(value.amount.denom)
    .pushU128(BigInt(value.amount.amount))
    .pushField(value.reserveCommitment)
    .finish();
